/**
 * RecordTablePicker — reużywalny picker rekordu w formie MODALNEJ TABELI (picker-tabela,
 * gałąź `feat/pola-dodatkowe-na-listach-i-picker`).
 *
 * W przeciwieństwie do `RelationPicker` (dropdown + tekstowe wyszukiwanie, jeden rekord na
 * raz widoczny) ten picker osadza pełny `ConfigurableTable` w modalu (`@/components/Modal`) —
 * kolumny (core + pola dodatkowe) budowane z katalogu pól kartoteki (`GET /system-modules/
 * {moduleSlug}/fields`), dane z listy encji z projekcją `?columns=` (kontrakt: `id` zawsze +
 * żądane core-pola pod kluczami camelCase + `ext: { <extSlug>: value }` dla żądanych pól
 * dodatkowych — patrz `apps/api/app/api/contractors/route.ts`).
 *
 * W pełni obsługiwane encje: wszystkie 5 kartotek płaskich z `coreFieldMap`
 * (contractors/employees/users/products/companies — `lib/system-entity-adapters.ts`)
 * oraz `documents` (adapter z `classDimension` — patrz niżej). Komunikat braku wsparcia
 * zostaje wyłącznie dla slugów bez adaptera (ścieżka defensywna).
 *
 * Selekcja:
 *  - single (`multiple` nieustawione/`false`) — klik w wiersz -> `onChange(id, row)` + zamknięcie.
 *  - multi (`multiple=true`) — checkboxy (`ConfigurableTable.selectable`) + przycisk „Wybierz"
 *    w stopce modala -> `onChange(ids, rows)`. Wybrane wiersze trzymane w mapie
 *    `id -> pełny wiersz` (nie tylko lista idków), żeby zaznaczenie rozłożone na kilku
 *    stronach nie traciło pełnych obiektów po przewinięciu do kolejnej strony.
 *
 * Zawsze zwracamy PEŁNY obiekt wiersza (nie tylko id) — potrzebne dla późniejszego
 * autouzupełnienia pól siostrzanych na formatce (np. telefon/email po wyborze kontrahenta).
 *
 * Filtrowanie: kategoria filtra per kolumna (`headerFilter.category`) liczona z `fieldType`
 * katalogu przez `mapExtFieldTypeToFilterCategory` (`@/lib/ext-list-columns` — jedna reguła
 * współdzielona z listami core+ext, bez duplikacji). Prosty filtr-wiersz (`columnFilters`,
 * operator `contains`) dostają WYŁĄCZNIE kolumny kategorii `text` — inne typy (data/liczba/
 * select/boolean) filtrowalne WYŁĄCZNIE przez popup nagłówka (`columnFilters`/`headerFilters`
 * oba kontrolowane, mirror `apps/admin/src/pages/Contractors.tsx`). Powód: backend
 * (`filterToSQL` w `apps/api/lib/contractors/apply-header-filters.ts`) nie ma type-gate dla
 * kolumn core — `contains` (ILIKE) na kolumnie PG `date`/`numeric` rzuca 42883 (500
 * nieobsłużony, poza blokiem mapowania błędów listy). Kolumny ext — `buildExtColumns`
 * (ten sam builder co listy), własna eligibility `isFilterable && category !== 'none'`.
 * Oba stany (`columnFilters` + `headerFilters`) foldowane w jedno `?headerFilters=` w
 * `listQuery.queryFn` niżej — kolumna BEZ `headerFilter.fieldSlug` jest cicho pomijana
 * (bez fallbacku do gołego `columnId` — to był wektor tego buga: core-kolumny nie miały
 * `headerFilter` wcale, fallback wysyłał `contains` na dowolne pole niezależnie od typu).
 *
 * `layoutTableId` vs `distinctTableId` (dwa ODDZIELNE identyfikatory przekazywane do
 * `ConfigurableTable`, patrz komentarz przy ich obliczeniu niżej) — `tableId`
 * (=`layoutTableId`, prefiks `record-table-picker-`) jest cache key `useTableLayout`;
 * MUSI być unikalny względem tableId realnych stron listy, bo `persistConfig={false}`
 * wyłącza tylko fetch, nie odczyt już zcache'owanych danych pod tym samym react-query
 * key we WSPÓLNYM `QueryClient` (drugi, prawdziwy widok listy tej samej encji w tej
 * samej sesji SPA by "zatruł" picker cudzym layoutem — ukryte kolumny/tryb kanban).
 * `distinctTableId` (bez prefiksu) zostaje literalnym kluczem `buildEndpointUrl`
 * (podpowiedzi popupu) — endpoint `/distinct` jest bezstanowy, nie wymaga unikalności.
 *
 * Paginacja: endpoint listy nie zwraca `total` (`{ contractors, limit, offset }`), więc
 * wbudowana `TablePagination` (wymaga `totalItems`) nie ma zastosowania — heurystyka
 * prev/next „strona pełna = może być kolejna" (mirror `RecordLinkTargetPickerModal`).
 *
 * Prefetch wybranych rekordów: kartoteki nie wspierają pobrania „po idkach", więc
 * wstępne zaznaczenie (`value` przy otwarciu) dociągamy per-id przez endpoint szczegółu
 * (mirror `RelationPicker`) — inaczej wiersze zaznaczone spoza pierwszej strony nie miałyby
 * pełnego obiektu do zwrócenia przy Confirm bez zmiany zaznaczenia. Adaptery z `resolveByIds`
 * (dziś: WYŁĄCZNIE `documents`) dociągają batch-em (`?ids=`) zamiast pętli per-id.
 *
 * `documents` (F20) — adapter z `classDimension` (`lib/system-entity-adapters.ts`) renderuje
 * DODATKOWO selektor klasy nad tabelą: domyślnie (brak wybranej klasy) picker pokazuje
 * WSZYSTKIE klasy naraz z KOLUMNAMI STAŁYMI (`DOCUMENT_SHARED_COLUMNS` — bez katalogu pól,
 * `/system-modules/documents/fields` nie istnieje, bez `?columns=`); po wybraniu klasy picker
 * przełącza się na zwykłą ścieżkę katalog+`?columns=` (`classDimension.resolveForClass`),
 * identyczną co do kształtu z `contractors` (włącznie z type-gated filtrowaniem — patrz
 * akapit „Filtrowanie" wyżej: `fieldType` z katalogu klasy decyduje o kategorii). Jedyna
 * różnica: w domyślnym widoku „wszystkie klasy" (bez katalogu pól — `DOCUMENT_SHARED_COLUMNS`,
 * kolumny STAŁE) `id` kolumny jest camelCase (`DOCUMENT_SHARED_COLUMNS.key`), a backend
 * filtruje po snake_case (`SHARED_CORE_COLUMN_MAP`) — tłumaczenie idzie przez
 * `column.headerFilter.fieldSlug` (`DOCUMENT_SHARED_COLUMNS.filterField`), identycznie jak
 * `col.headerFilter?.fieldSlug` w `apps/admin/src/pages/Contractors.tsx`. Bez `fieldType` z
 * API w tym widoku (brak katalogu) — `documentDate`/`receivedDate` (PG `date`) i
 * `amountNet`/`amountVat`/`amountGross` (PG `numeric`) są ręcznie wyłączone z filtrowania
 * (`DOCUMENT_SHARED_UNSAFE_CONTAINS_KEYS` niżej) z tego samego powodu co reszta pliku — ILIKE
 * na tych typów rzuca 500. Kolumna `class` (liczona z `row.class`) nie ma `filterField` —
 * niefiltrowalna, backend i tak by ją cicho zignorował.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Pencil, Plus } from 'lucide-react';

import { Button } from '@/components/Button';
import type {
  ColumnDefinition,
  ColumnFilters,
  ColumnHeaderFilter,
  ColumnHeaderFilters,
} from '@/components/configurable-table';
import { ConfigurableTable } from '@/components/configurable-table';
import { IconButton } from '@/components/IconButton';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { api } from '@/lib/api';
import {
  buildExtColumns,
  CORE_REFERENCE_SLUGS_WITH_LABEL,
  type ExtRefLabels,
  mapExtFieldTypeToFilterCategory,
  renderBooleanCell,
  renderCoreReferenceCell,
  useExtRefLabelsGetter,
} from '@/lib/ext-list-columns';
import {
  DOCUMENT_SHARED_COLUMNS,
  documentClassLabelKey,
  getSystemEntityAdapter,
} from '@/lib/system-entity-adapters';

const TEST_ID_DEFAULT = 'record-table-picker';
const PAGE_SIZE = 20;
const FILTER_DEBOUNCE_MS = 300;

/**
 * `documentDate`/`receivedDate` (PG `date`) i `amountNet`/`amountVat`/`amountGross` (PG
 * `numeric`) w tabeli `documents` (`apps/api/drizzle/tenant/schema.ts`) — niebezpieczne dla
 * prostego filtra "kontains" (ILIKE), który backend (`filterToSQL` w `apps/api/lib/
 * contractors/apply-header-filters.ts`) wysyła BEZ type-gate dla kolumn core -> PG 42883 ->
 * 500. Widok „wszystkie klasy" (`DOCUMENT_SHARED_COLUMNS`) nie ma katalogu pól — bez
 * `fieldType` z API do policzenia kategorii automatycznie, stąd statyczna whitelist
 * wyjątków; reszta kolumn wspólnych jest varchar/text/char (bezpieczna dla ILIKE).
 */
const DOCUMENT_SHARED_UNSAFE_CONTAINS_KEYS = new Set<string>([
  'documentDate',
  'receivedDate',
  'amountNet',
  'amountVat',
  'amountGross',
]);

/**
 * Core-referencje (FK UUID) z resolverem etykiety w `extRefLabels`. Definicja przeniesiona
 * do `@/lib/ext-list-columns` (jedno źródło prawdy współdzielone z `buildCoreColumns` list) —
 * tu re-eksport dla dotychczasowych importerów (m.in. `RecordTablePicker.test.tsx`).
 *
 * UWAGA — włączenie slugu tam NIE WYSTARCZY samo w sobie: kolumna renderuje się z etykietą
 * TYLKO gdy slug jest RÓWNIEŻ (a) w `?columns=` whiteliście trasy backendu danej encji/klasy
 * ORAZ (b) w `coreFieldMap` adaptera (`SYSTEM_ENTITY_ADAPTERS`/`STANDARD_CORE_FIELD_MAP`/
 * `INVOICE_CORE_FIELD_MAP`, `@/lib/system-entity-adapters.ts` — inaczej `visibleFields`
 * poniżej odrzuca pole zanim dotrze do tego zbioru).
 */
export { CORE_REFERENCE_SLUGS_WITH_LABEL };

/** Wiersz zwrócony przez encję z projekcją `?columns=` — `id` zawsze + żądane core/ext pola. */
export interface RecordTablePickerRow {
  id: string;
  /** Pola dodatkowe (ext), kluczowane po slugu — doklejane przez backend w trybie `?columns=`. */
  ext?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Pole z katalogu `GET /system-modules/{moduleSlug}/fields` — kształt zgodny z
 * `SystemModuleFieldDef` (`@/lib/ext-list-columns`, wymagany przez `buildExtColumns`), żeby
 * przekazać katalog bez castowania.
 */
interface SystemModuleFieldDTO {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
  showInList: boolean;
  sortOrder: number;
  isFilterable: boolean;
  options: Array<{ value: string; label: string; color?: string | null }> | null;
}

interface FieldsResponse {
  fields: SystemModuleFieldDTO[];
}

/** Kształt lokalny `listQuery.data` — wiersze + sibling `extRefLabels` (kontrakt
 *  `?columns=`, patrz `ExtRefLabels`), rozdzielone z surowej odpowiedzi backendu w
 *  `queryFn` (endpoint per encja zwraca wiersze pod różnym kluczem —
 *  `adapter.listResponseKey` — ale `extRefLabels` zawsze pod tym samym kluczem). */
interface ListQueryResult {
  rows: RecordTablePickerRow[];
  extRefLabels?: ExtRefLabels;
}

interface RecordTablePickerCommonProps {
  /** Klucz w `SYSTEM_ENTITY_ADAPTERS` (np. `'contractors'`). */
  entity: string;
  open: boolean;
  onClose: () => void;
  /** Zawężenie listy do jednej firmy (`?companyId=`). */
  companyId?: string;
  'data-testid'?: string;
}

/**
 * Akcje pomocnicze okna wyboru (opt-in, TYLKO single-pick) — „okno wyboru kontrahenta"
 * na dokumencie (spotkanie FK 31.08). Gdy podane, picker renderuje:
 *  - kolumnę akcji per wiersz: „otwórz kartotekę" (`onOpenCard`) i „edytuj" (`onEditRecord`),
 *  - przycisk „dodaj nowego" (`onCreateNew`) w pasku nad tabelą.
 * Klik akcji NIE wybiera wiersza (stopPropagation — nie odpala `onChange`/zamknięcia).
 * Etykiety dostarcza KONSUMENT (picker jest generyczny — dla kontrahenta/dokumentu inne
 * teksty), więc labelki są wymagane obok handlerów. Pominięty handler = brak danej akcji.
 * Domyślnie brak propa → zachowanie bajt-w-bajt jak dotąd (obecni konsumenci go nie podają).
 */
export interface RecordTablePickerActions {
  onOpenCard?: (row: RecordTablePickerRow) => void;
  onEditRecord?: (row: RecordTablePickerRow) => void;
  onCreateNew?: () => void;
  labels: {
    openCard: string;
    editRecord: string;
    createNew: string;
  };
}

export interface RecordTablePickerSingleProps extends RecordTablePickerCommonProps {
  multiple?: false;
  value?: string | null;
  onChange: (id: string, row: RecordTablePickerRow) => void;
  /** Akcje pomocnicze okna wyboru (otwórz/edytuj/dodaj) — patrz `RecordTablePickerActions`. */
  recordActions?: RecordTablePickerActions;
  /**
   * Tryb osadzony (TYLKO single-pick) — gdy `true`, komponent zwraca SAMO wnętrze
   * (selektor klasy + tabela + paginacja) BEZ własnego `<Modal>` i BEZ footera. Używa go
   * `RecordLinkTargetPickerModal`, montując picker per zakładka typu celu WEWNĄTRZ swojego
   * okna (uniknięcie modal-in-modal); wybór następuje kliknięciem wiersza. Świadomie
   * NIEDOSTĘPNY w trybie multi (`RecordTablePickerMultipleProps` go nie ma) — bez footera
   * nie byłoby jak potwierdzić zaznaczenia. Domyślnie `false`/`undefined` → pełne okno
   * modalne, zachowanie bajt-w-bajt jak dotychczas (obecni konsumenci go nie podają).
   */
  embedded?: boolean;
}

export interface RecordTablePickerMultipleProps extends RecordTablePickerCommonProps {
  multiple: true;
  value?: string[] | null;
  onChange: (ids: string[], rows: RecordTablePickerRow[]) => void;
}

/** Dyskryminowana unia po `multiple` — `onChange` ma precyzyjny kształt per tryb. */
export type RecordTablePickerProps = RecordTablePickerSingleProps | RecordTablePickerMultipleProps;

function isMultipleProps(p: RecordTablePickerProps): p is RecordTablePickerMultipleProps {
  return p.multiple === true;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const NO_LEADING_COLUMNS: readonly string[] = [];

export function RecordTablePicker(props: RecordTablePickerProps) {
  const { entity, open, onClose, companyId } = props;
  const multiple = isMultipleProps(props);
  // `embedded` żyje wyłącznie na wariancie single-pick (patrz `RecordTablePickerSingleProps`)
  // — w multi z definicji `false` (narrowing przez type-guard, nie przez `multiple`).
  const embedded = isMultipleProps(props) ? false : props.embedded === true;
  // Akcje pomocnicze okna wyboru (otwórz/edytuj/dodaj) — TYLKO single-pick, opt-in.
  const recordActions = isMultipleProps(props) ? undefined : props.recordActions;
  const { t } = useTranslation();
  const testId = props['data-testid'] ?? TEST_ID_DEFAULT;

  const adapter = getSystemEntityAdapter(entity);
  const classDimension = adapter?.classDimension;
  const supportsColumns = !!adapter?.coreFieldMap || !!classDimension;
  const leadingColumns = adapter?.pickerLeadingColumns ?? NO_LEADING_COLUMNS;

  const [page, setPage] = useState(0);
  // Prosty filtr-wiersz (kolumny kategorii `text` — patrz nagłówek pliku); debounced (typing).
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, FILTER_DEBOUNCE_MS);
  // Popup nagłówka (wszystkie kategorie filtrowalne); zmiana dyskretna (przycisk „Zastosuj"
  // w popupie), bez debounce — mirror `apps/admin/src/pages/Contractors.tsx`.
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [selectedRows, setSelectedRows] = useState<Map<string, RecordTablePickerRow>>(new Map());
  // Selektor klasy (WYŁĄCZNIE adaptery z `classDimension`, dziś: `documents`) — `null` =
  // domyślny widok „wszystkie klasy" (kolumny stałe, brak katalogu pól).
  const [selectedClass, setSelectedClass] = useState<string | null>(null);

  // Reset stanu lokalnego przy każdym otwarciu (mirror `RecordLinkTargetPickerModal`) —
  // kolejne otwarcie zawsze startuje "na czysto"; wstępne zaznaczenie z `value` obsłużone
  // niżej przez prefetch + seed efekt.
  useEffect(() => {
    if (!open) return;
    setPage(0);
    setColumnFilters({});
    setHeaderFilters({});
    setSelectedClass(null);
  }, [open]);

  // Rozwiązanie klasy wybranej w selektorze (WYŁĄCZNIE gdy `classDimension` obecne I
  // `selectedClass` ustawione) — `null` w domyślnym widoku „wszystkie klasy".
  const classResolution = useMemo(() => {
    if (!classDimension || !selectedClass) return null;
    return classDimension.resolveForClass(selectedClass);
  }, [classDimension, selectedClass]);

  // Katalog pól: dla adapterów płaskich — zawsze `adapter.moduleSlug`; dla `classDimension` —
  // WYŁĄCZNIE gdy klasa wybrana (domyślny widok „wszystkie klasy" nie ma katalogu, patrz
  // nagłówek pliku), moduleSlug = classResolution.moduleSlug.
  const catalogModuleSlug = classDimension
    ? (classResolution?.moduleSlug ?? null)
    : (adapter?.moduleSlug ?? null);
  const effectiveCoreFieldMap = classDimension
    ? (classResolution?.coreFieldMap ?? null)
    : (adapter?.coreFieldMap ?? null);

  // Katalog pól kartoteki (lub klasy dokumentu) — źródło kolumn (core + ext). Nieużywany
  // w domyślnym widoku `documents` „wszystkie klasy" (catalogModuleSlug=null → disabled).
  const fieldsQuery = useQuery<FieldsResponse>({
    queryKey: ['record-table-picker-fields', catalogModuleSlug],
    queryFn: async () => {
      const res = await api.get<FieldsResponse>(`/system-modules/${catalogModuleSlug}/fields`);
      return res.data;
    },
    enabled: open && supportsColumns && !!catalogModuleSlug,
    staleTime: 60_000,
  });

  // Pola widoczne na liście — core pole musi mieć wpis w `coreFieldMap` (inaczej nie wiemy
  // pod jakim kluczem czytać wartość z projektowanego wiersza; ciche pominięcie, nie błąd).
  // Core pole typu `reference` (FK UUID — np. `corrected_invoice_id`/`vendor_bank_account_id`
  // w katalogu klas dokumentu) jest RÓWNIEŻ pomijane, CHYBA że jego slug ma resolver etykiety
  // (`CORE_REFERENCE_SLUGS_WITH_LABEL` wyżej — `owner_user_id`/`assigned_user_id`/`company_id`/
  // `primary_category_id`) — bez resolvera kolumna wyrenderowałaby surowy UUID, więc reszta
  // referencji core zostaje wykluczona. Pola z resolverem renderują się przez
  // `renderCoreReferenceCell` (patrz `coreColumns` niżej), etykieta z `extRefLabels`.
  const visibleFields = useMemo(() => {
    const fields = fieldsQuery.data?.fields ?? [];
    const coreFieldMap = effectiveCoreFieldMap;
    return fields
      .filter((f) => f.showInList)
      .filter((f) => !f.isCore || (coreFieldMap && f.slug in coreFieldMap))
      .filter(
        (f) =>
          !f.isCore || f.fieldType !== 'reference' || CORE_REFERENCE_SLUGS_WITH_LABEL.has(f.slug),
      )
      .sort((a, b) => {
        // Kolumny rozpoznawcze adaptera (np. nazwa + NIP) na początek, w zadanej kolejności —
        // reszta w kolejności katalogu. Bez listy: sama kolejność katalogu.
        const ia = leadingColumns.indexOf(a.slug);
        const ib = leadingColumns.indexOf(b.slug);
        if (ia !== -1 || ib !== -1) {
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        }
        return a.sortOrder - b.sortOrder;
      });
  }, [fieldsQuery.data, effectiveCoreFieldMap, leadingColumns]);

  // `getRefLabels` — getter stabilny (patrz `useExtRefLabelsGetter`, `@/lib/ext-list-
  // columns`), dostarcza `listQuery.data?.extRefLabels` do renderCell kolumn `reference`
  // bez wchodzenia w zależności `columns` (aktualizowany niżej, PO `listQuery`, żeby
  // uniknąć TDZ — `columns` jest zdefiniowane PRZED `listQuery`, patrz `requestedColumnSlugs`
  // → `listQuery.queryKey` niżej).
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();

  const columns = useMemo<ColumnDefinition<RecordTablePickerRow>[]>(() => {
    // Kolumna akcji per wiersz (opt-in, okno wyboru) — „otwórz kartotekę" / „edytuj".
    // Klik akcji zatrzymuje propagację, więc NIE odpala wyboru wiersza (`onRowClick`).
    // `sticky:'right'` + `hideable:false` — zawsze widoczna, przyklejona po prawej.
    const actionColumn: ColumnDefinition<RecordTablePickerRow> | null =
      recordActions && (recordActions.onOpenCard || recordActions.onEditRecord)
        ? {
            id: '__record_actions__',
            accessor: () => '',
            header: '',
            sortable: false,
            filterable: false,
            hideable: false,
            align: 'right',
            sticky: 'right',
            renderCell: (_value, row) => (
              <div
                className="flex items-center justify-end gap-1"
                onClick={(e) => e.stopPropagation()}
                role="presentation"
              >
                {recordActions.onOpenCard && (
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label={recordActions.labels.openCard}
                    title={recordActions.labels.openCard}
                    onClick={(e) => {
                      e.stopPropagation();
                      recordActions.onOpenCard?.(row);
                    }}
                    data-testid={`${testId}-row-open`}
                  >
                    <ExternalLink aria-hidden="true" />
                  </IconButton>
                )}
                {recordActions.onEditRecord && (
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label={recordActions.labels.editRecord}
                    title={recordActions.labels.editRecord}
                    onClick={(e) => {
                      e.stopPropagation();
                      recordActions.onEditRecord?.(row);
                    }}
                    data-testid={`${testId}-row-edit`}
                  >
                    <Pencil aria-hidden="true" />
                  </IconButton>
                )}
              </div>
            ),
          }
        : null;
    const withActions = (
      base: ColumnDefinition<RecordTablePickerRow>[],
    ): ColumnDefinition<RecordTablePickerRow>[] => (actionColumn ? [...base, actionColumn] : base);

    // `documents`, domyślny widok „wszystkie klasy" — kolumny STAŁE (brak katalogu pól,
    // patrz nagłówek pliku), nagłówki z i18n (brak `name` gotowego z backendu jak w katalogu).
    if (classDimension && !selectedClass) {
      const sharedCols = DOCUMENT_SHARED_COLUMNS.map(
        (c): ColumnDefinition<RecordTablePickerRow> => {
          // Bez `fieldType` z API (brak katalogu w tym widoku) — kolumny data/kwota z whitelisty
          // (`DOCUMENT_SHARED_UNSAFE_CONTAINS_KEYS`, patrz nagłówek pliku) są NIEFILTROWALNE
          // (ani prosty filter-row, ani popup) — reszta kolumn wspólnych zostaje filtrowalna
          // jak dotychczas (varchar/text/char, bezpieczne dla ILIKE).
          // `filterField` czytane raz do lokalnego const — narrowing `string | undefined` ->
          // `string` bez `!` (kontrola przepływu przez `isFilterable && filterField` w ternary).
          const filterField = c.filterField;
          const isFilterable = !!filterField && !DOCUMENT_SHARED_UNSAFE_CONTAINS_KEYS.has(c.key);
          return {
            id: c.key,
            accessor:
              c.key === 'class'
                ? (row: RecordTablePickerRow) => {
                    const labelKey = documentClassLabelKey(row.class);
                    return labelKey ? t(labelKey) : '—';
                  }
                : c.key,
            header: t(c.labelKey),
            sortable: false,
            filterable: isFilterable,
            // `id`/`key` jest camelCase (klucz odpowiedzi), backend filtruje po snake_case
            // (`SHARED_CORE_COLUMN_MAP`) — `fieldSlug` tłumaczy jedno na drugie przy budowie
            // `?headerFilters=` (patrz `listQuery.queryFn` niżej). `category: 'none'` — bez
            // ikonki popupu 3-trybowego, mamy tylko prosty filter-row.
            headerFilter:
              isFilterable && filterField
                ? { category: 'none', fieldSlug: filterField }
                : undefined,
          };
        },
      );
      return withActions(sharedCols);
    }
    const coreFieldMap = effectiveCoreFieldMap;
    if (!coreFieldMap) return [];
    const coreColumns = visibleFields
      .filter((f) => f.isCore)
      .map((f): ColumnDefinition<RecordTablePickerRow> => {
        const responseKey = coreFieldMap[f.slug] ?? f.slug;
        // Kategoria z typu pola — jedna reguła współdzielona z listami (core+ext), patrz
        // nagłówek pliku. Tylko `text` dostaje prosty filter-row; resztę (data/liczba/
        // select/boolean) filtrujemy WYŁĄCZNIE przez popup nagłówka (`category !== 'none'`
        // wystarcza — `ConfigurableTable` renderuje ikonkę popupu na tym warunku).
        const filterCategory = mapExtFieldTypeToFilterCategory(f.fieldType);
        return {
          id: f.slug,
          accessor: responseKey,
          header: f.name,
          sortable: false,
          filterable: filterCategory === 'text',
          // Core `boolean` — bez tego generyczny fallback `String(value ?? '-')`
          // (`ConfigurableTable`, brak `renderCell`) drukowałby surowy tekst `true`/`false`.
          // Ten sam helper co kolumny ext (`buildExtColumns` → `renderExtFieldValue`) —
          // jedno źródło prawdy dla renderu boolean, core i ext.
          ...(f.fieldType === 'boolean'
            ? { renderCell: (value: unknown) => renderBooleanCell(value, t) }
            : {}),
          // Core `reference` — dotarł tutaj WYŁĄCZNIE gdy slug ma resolver etykiety
          // (`CORE_REFERENCE_SLUGS_WITH_LABEL`, filtr w `visibleFields` wyżej) — bez
          // `renderCell` generyczny fallback drukowałby surowy UUID. `company_id` dostaje
          // `fallback: 'dash'` (mirror list dokumentów/Employees — nierozwiązywalna firma,
          // np. brak `companies.view`, renderuje „—" nie surowy UUID); resztę (opiekun/
          // kategoria) zostaje przy zatwierdzonym kontrakcie `'id'`.
          ...(f.fieldType === 'reference'
            ? {
                renderCell: renderCoreReferenceCell(
                  f.slug,
                  getRefLabels,
                  t,
                  f.slug === 'company_id' ? 'dash' : 'id',
                ),
              }
            : {}),
          ...(filterCategory !== 'none'
            ? {
                headerFilter: {
                  category: filterCategory,
                  fieldSlug: f.slug,
                  fieldType: f.fieldType,
                  ...(filterCategory === 'select' && f.options
                    ? {
                        options: f.options.map((opt) => ({
                          value: opt.value,
                          label: opt.label,
                          color: opt.color ?? undefined,
                        })),
                      }
                    : {}),
                },
              }
            : {}),
        };
      });
    // Pola dodatkowe (ext) — ten sam builder co listy (`buildExtColumns` filtruje `!isCore`
    // wewnętrznie, eligibility `isFilterable && category !== 'none'`, patrz `@/lib/ext-list-
    // columns`). Core kolumny zawsze pierwsze, ext dołożone na końcu — mirror kolejności z
    // list stron (np. `Contractors.tsx`: `[...static core, ...extColumns]`).
    const extColumns = buildExtColumns<RecordTablePickerRow>(visibleFields, t, getRefLabels);
    return withActions([...coreColumns, ...extColumns]);
  }, [
    visibleFields,
    effectiveCoreFieldMap,
    classDimension,
    selectedClass,
    t,
    getRefLabels,
    recordActions,
    testId,
  ]);

  const requestedColumnSlugs = useMemo(() => visibleFields.map((f) => f.slug), [visibleFields]);

  const listQuery = useQuery<ListQueryResult>({
    queryKey: [
      'record-table-picker-list',
      adapter?.slug,
      selectedClass,
      companyId ?? null,
      requestedColumnSlugs,
      headerFilters,
      debouncedColumnFilters,
      page,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (requestedColumnSlugs.length > 0) params.set('columns', requestedColumnSlugs.join(','));
      if (companyId) params.set('companyId', companyId);
      // `documents`: `?class=` tylko gdy klasa wybrana (brak = wszystkie aktywne klasy).
      if (classDimension && selectedClass) params.set('class', selectedClass);

      // Filtry -> `?headerFilters=` — (1) popup nagłówka (`headerFilters`, mode=values/
      // condition/range) + (2) prosty filter-row (`debouncedColumnFilters`, mode=condition,
      // operator=contains) — mirror `apps/admin/src/pages/Contractors.tsx`. `field` ZAWSZE
      // z `column.headerFilter.fieldSlug` — BEZ fallbacku do gołego `columnId` (kolumna bez
      // `headerFilter` jest cicho pomijana). To był wektor buga tego pliku: core-kolumny
      // wcześniej nie miały `headerFilter` wcale, fallback do `columnId` wysyłał `contains`
      // na dowolne pole niezależnie od typu (data/kwota -> PG 42883 -> 500, patrz nagłówek
      // pliku). Teraz KAŻDA filtrowalna kolumna (`filterable` lub `headerFilter.category !==
      // 'none'`) ma `headerFilter.fieldSlug` ustawiony explicité przy budowie `columns`.
      const headerFiltersPayload: ColumnHeaderFilter[] = [];
      for (const [columnId, filter] of Object.entries(headerFilters)) {
        const col = columns.find((c) => c.id === columnId);
        const fieldSlug = col?.headerFilter?.fieldSlug;
        if (fieldSlug) headerFiltersPayload.push({ ...filter, field: fieldSlug });
      }
      for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
        if (val == null || val === '') continue;
        const col = columns.find((c) => c.id === columnId);
        const fieldSlug = col?.headerFilter?.fieldSlug;
        if (!fieldSlug) continue;
        headerFiltersPayload.push({
          field: fieldSlug,
          mode: 'condition',
          operator: 'contains',
          conditionValue: String(val),
        });
      }
      if (headerFiltersPayload.length > 0) {
        params.set('headerFilters', JSON.stringify(headerFiltersPayload));
      }

      const listPath = classResolution?.listPath ?? adapter!.listPath;
      const res = await api.get<Record<string, unknown>>(`${listPath}?${params.toString()}`);
      const rows = (res.data[adapter!.listResponseKey] as RecordTablePickerRow[] | undefined) ?? [];
      const extRefLabels = res.data.extRefLabels as ExtRefLabels | undefined;
      return { rows, extRefLabels };
    },
    // Domyślny widok „wszystkie klasy" (`classDimension && !selectedClass`) nie ma katalogu —
    // enabled natychmiast. Reszta (flat adaptery + `documents` z klasą wybraną) czeka aż
    // katalog dostarczy przynajmniej jedną widoczną kolumnę (mirror dotychczasowego kontraktu
    // `?columns=` z `/api/contractors` — pusty `?columns=` to tryb projekcji z zerem pól).
    enabled:
      open &&
      supportsColumns &&
      (classDimension && !selectedClass ? true : requestedColumnSlugs.length > 0),
    staleTime: 5_000,
  });

  const rows = listQuery.data?.rows ?? [];
  setRefLabels(listQuery.data?.extRefLabels);
  const canGoNext = rows.length === PAGE_SIZE;
  const canGoPrev = page > 0;

  const initialSelectedIds = useMemo<string[]>(() => {
    if (!props.value) return [];
    return Array.isArray(props.value) ? props.value : [props.value];
  }, [props.value]);

  // Prefetch wybranych rekordów z `value` — kartoteki (adaptery bez `resolveByIds`) nie
  // wspierają pobrania "po idkach" (patrz nagłówek `lib/system-entity-adapters.ts`), więc
  // dociągamy szczegół per id (mirror `RelationPicker`). Adaptery z `resolveByIds` (dziś:
  // WYŁĄCZNIE `documents`) dociągają batch-em w jednym zapytaniu.
  const prefetchQuery = useQuery<RecordTablePickerRow[]>({
    queryKey: ['record-table-picker-prefetch', adapter?.slug, ...initialSelectedIds],
    queryFn: async () => {
      if (!adapter || initialSelectedIds.length === 0) return [];
      if (adapter.resolveByIds) {
        try {
          const res = await api.get<Record<string, unknown>>(
            adapter.resolveByIds.path(initialSelectedIds),
          );
          const fetchedRows =
            (res.data[adapter.resolveByIds.responseKey] as Record<string, unknown>[] | undefined) ??
            [];
          return fetchedRows.map((raw) => ({ ...raw, id: String(raw.id) }) as RecordTablePickerRow);
        } catch {
          return [];
        }
      }
      const detailPath = adapter.detailPath;
      const detailResponseKey = adapter.detailResponseKey;
      if (!detailPath || !detailResponseKey) return [];
      const fetched = await Promise.all(
        initialSelectedIds.map(async (id) => {
          try {
            const res = await api.get<Record<string, unknown>>(detailPath(id));
            const raw = res.data[detailResponseKey] as Record<string, unknown> | undefined;
            if (!raw) return null;
            return { ...raw, id: String(raw.id ?? id) } as RecordTablePickerRow;
          } catch {
            return null;
          }
        }),
      );
      return fetched.filter((r): r is RecordTablePickerRow => r !== null);
    },
    // Prefetch tylko w trybie wielokrotnym — w single `value` jest podświetlane
    // wprost (`singleValue === row.id`), a `onChange` zwraca świeżo kliknięty wiersz,
    // więc dociągnięty szczegół i tak nie jest tam konsumowany (unikamy zbędnego GET).
    enabled: open && multiple && !!adapter && initialSelectedIds.length > 0,
    staleTime: 60_000,
  });

  // Seed `selectedRows` z prefetchu przy każdym otwarciu (razem z resetem stanu powyżej).
  useEffect(() => {
    if (!open) return;
    setSelectedRows(() => {
      const next = new Map<string, RecordTablePickerRow>();
      for (const row of prefetchQuery.data ?? []) next.set(row.id, row);
      return next;
    });
  }, [open, prefetchQuery.data]);

  const selectedIds = useMemo(() => Array.from(selectedRows.keys()), [selectedRows]);
  const singleValue = isMultipleProps(props) ? null : (props.value ?? null);

  function handleSelectionChange(ids: string[]) {
    setSelectedRows((prev) => {
      const next = new Map<string, RecordTablePickerRow>();
      for (const id of ids) {
        const existing = prev.get(id) ?? rows.find((r) => r.id === id);
        if (existing) next.set(id, existing);
      }
      return next;
    });
  }

  function handleRowClick(row: RecordTablePickerRow) {
    if (isMultipleProps(props)) return;
    props.onChange(row.id, row);
    onClose();
  }

  function handleConfirmMulti() {
    if (!isMultipleProps(props)) return;
    props.onChange(Array.from(selectedRows.keys()), Array.from(selectedRows.values()));
    onClose();
  }

  const footer = multiple ? (
    <div className="flex w-full items-center justify-between gap-2">
      <span className="text-sm text-text-secondary" data-testid={`${testId}-selected-count`}>
        {t('ui.recordTablePicker.selectedCount', { count: selectedIds.length })}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} data-testid={`${testId}-cancel`}>
          {t('ui.recordTablePicker.cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleConfirmMulti}
          disabled={selectedIds.length === 0}
          data-testid={`${testId}-confirm`}
        >
          {t('ui.recordTablePicker.confirm')}
        </Button>
      </div>
    </div>
  ) : (
    <div className="flex w-full items-center justify-end">
      <Button variant="ghost" size="sm" onClick={onClose} data-testid={`${testId}-cancel`}>
        {t('ui.recordTablePicker.cancel')}
      </Button>
    </div>
  );

  const loadError = fieldsQuery.isError || listQuery.isError;

  // DWA różne identyfikatory dla `ConfigurableTable` — rozdzielone (nie jeden `tableId`),
  // bo mają NIEKOMPATYBILNE wymagania:
  //
  // `layoutTableId` -> prop `tableId` (cache key `useTableLayout`, react-query
  // `['table-layouts', tableId]`). MUSI być unikalny/nie-kolidujący z tableId realnej
  // strony listy (np. `Contractors.tsx` używa gołego `'contractors'`) — `persistConfig=
  // {false}` niżej wyłącza WYŁĄCZNIE fetch (`enabled`), NIE chroni przed odczytem: gdy
  // ten sam react-query `QueryClient` ma już zcache'owany wpis pod `['table-layouts',
  // 'contractors']` (bo user wcześniej otworzył prawdziwą listę kontrahentów w tej samej
  // sesji SPA — jeden QueryClient na cały tenant scope), `useQuery` z `enabled:false` I
  // TAK zwróci ten zcache'owany wynik od razu (cache nie sprawdza `enabled`, tylko fetch).
  // Picker widziałby WIDOCZNOŚĆ KOLUMN i `defaultViewMode` (np. kanban) z CUDZEGO layoutu
  // listy — prefiks `record-table-picker-` gwarantuje, że klucz cache pickera nigdy nie
  // pokrywa się z tableId żadnej realnej strony listy.
  //
  // `distinctTableId` -> prop `distinctTableId` (endpoint podpowiedzi popupu,
  // `useDistinctValues`/`buildEndpointUrl` w `ColumnHeaderFilterPopup`) — TU literalny
  // klucz MUSI zgadzać się z `buildEndpointUrl` (inaczej popup nie ma skąd pobrać
  // podpowiedzi — bezpieczny no-op, `enabled:false`, NIE błąd, patrz komentarz w
  // `columns` niżej), więc zostaje BEZ prefiksu. Kartoteki płaskie: `adapter.slug` (mirror
  // listy: 'contractors'/'users'/'companies'/'employees'/'products'). `documents` z
  // wybraną klasą: sam `selectedClass` (1:1 z literałami `buildEndpointUrl` —
  // 'purchase_invoices'/'sale_invoices'/'contracts'/'letters'/'commercial'/'internal'/'specjalne').
  // `documents` bez wybranej klasy („wszystkie klasy"): sentinel `'documents'` — żaden
  // literał go nie łapie (kolumny data/kwota są tam i tak niefiltrowalne, patrz `columns`
  // wyżej); `distinctTableId` NIE musi być unikalny (endpoint `/distinct` per encja/klasa
  // jest bezstanowy i identyczny niezależnie od tego, KTO go odpytuje).
  const layoutTableId = `record-table-picker-${selectedClass ?? adapter?.slug ?? 'documents'}`;
  const distinctTableId = classDimension
    ? (selectedClass ?? 'documents')
    : (adapter?.slug ?? TEST_ID_DEFAULT);

  // W oknie (nie embedded) tabela wypełnia wysokość okna: oba paski przewijania (poziomy
  // i pionowy) są wtedy zawsze w widocznym obszarze, a nagłówek kolumn przyklejony — user
  // od razu widzi, że kolumn jest więcej, bez zjeżdżania na koniec listy.
  const fill = !embedded;
  const content = (
    <div
      data-testid={testId}
      className={fill ? 'flex h-full min-h-0 flex-col gap-3' : 'flex flex-col gap-3'}
    >
      {recordActions?.onCreateNew && (
        <div className="flex items-center justify-end" data-testid={`${testId}-toolbar`}>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={14} aria-hidden="true" />}
            onClick={recordActions.onCreateNew}
            data-testid={`${testId}-create-new`}
          >
            {recordActions.labels.createNew}
          </Button>
        </div>
      )}
      {classDimension && (
        <div className="flex flex-wrap items-center gap-2" data-testid={`${testId}-class-bar`}>
          <Select
            value={selectedClass ?? ''}
            onChange={(e) => {
              setSelectedClass(e.target.value || null);
              setPage(0);
              // Zestaw kolumn (a więc i sensownych id filtrów) różni się między widokiem
              // „wszystkie klasy" i pojedynczą klasą — stare filtry nie mają zastosowania
              // do nowego zestawu kolumn.
              setColumnFilters({});
              setHeaderFilters({});
            }}
            options={[
              { value: '', label: t('ui.recordTablePicker.allClasses') },
              ...classDimension.classes.map((c) => ({ value: c.slug, label: t(c.labelKey) })),
            ]}
            aria-label={t('ui.recordTablePicker.classSelectLabel')}
            data-testid={`${testId}-class-select`}
          />
        </div>
      )}
      {adapter && supportsColumns ? (
        loadError ? (
          <p
            className="py-8 text-center text-sm text-danger-600"
            data-testid={`${testId}-load-error`}
          >
            {t('ui.recordTablePicker.loadError')}
          </p>
        ) : (
          <>
            <div className={fill ? 'min-h-0 flex-1' : undefined}>
              <ConfigurableTable<RecordTablePickerRow>
                tableId={layoutTableId}
                distinctTableId={distinctTableId}
                columns={columns}
                data={rows}
                isLoading={fieldsQuery.isLoading || listQuery.isLoading}
                persistConfig={false}
                configurable={false}
                showViewModeToggle={false}
                compact
                fillHeight={fill}
                wrapCells
                columnFilters={columnFilters}
                onColumnFiltersChange={(f) => {
                  setColumnFilters(f);
                  setPage(0);
                }}
                headerFilters={headerFilters}
                onHeaderFiltersChange={(f) => {
                  setHeaderFilters(f);
                  setPage(0);
                }}
                selectable={multiple}
                selectedIds={multiple ? selectedIds : []}
                onSelectionChange={multiple ? handleSelectionChange : undefined}
                onRowClick={multiple ? undefined : handleRowClick}
                rowClassName={(row) =>
                  !multiple && singleValue === row.id ? 'bg-accent-50 dark:bg-accent-900/20' : ''
                }
                emptyTitle={t('ui.recordTablePicker.emptyState')}
                testIdPrefix={testId}
              />
            </div>
            <div className="flex items-center justify-between text-sm text-text-secondary">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={!canGoPrev}
                data-testid={`${testId}-prev-page`}
              >
                {t('ui.recordTablePicker.prevPage')}
              </Button>
              <span data-testid={`${testId}-page-label`}>
                {t('ui.recordTablePicker.pageLabel', { page: page + 1 })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!canGoNext}
                data-testid={`${testId}-next-page`}
              >
                {t('ui.recordTablePicker.nextPage')}
              </Button>
            </div>
          </>
        )
      ) : (
        <p
          className="py-8 text-center text-sm text-text-muted"
          data-testid={`${testId}-not-supported`}
        >
          {t('ui.recordTablePicker.entityNotSupported')}
        </p>
      )}
    </div>
  );

  // Tryb osadzony: samo wnętrze, bez własnego okna (patrz `embedded` w propsach).
  if (embedded) return content;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('ui.recordTablePicker.title')}
      size="full"
      fixedHeight
      footer={footer}
    >
      {content}
    </Modal>
  );
}
